import {
  FC,
  PropsWithChildren,
  MouseEvent,
  useLayoutEffect,
  useRef
} from 'react'
import ReactDataSheet from 'react-datasheet'
import { Button } from '@progress/kendo-react-buttons'
import { Checkbox } from '@progress/kendo-react-inputs'

import 'react-datasheet/lib/react-datasheet.css'

import { ConfirmReplyActions, useAppDispatch, useAppSelector } from '../../../stores'
import { CodeName } from '../../../services/initializing/CodeName'
import { WorkQuantity } from '@gateway.Supplier'
import { Tooltip } from '@progress/kendo-react-tooltip'
// import { Authority } from '../../../gateway/system/Authority'
import hearingMessage from '../../../data/hearing-message.json'
import { BuildingSelect } from '../../../components/parts/BuildingSelect'
import { BuildingInfoGet } from '@gateway.Building'

export type CellValue = number | null

export interface CellData extends ReactDataSheet.Cell<CellData, number> {
  value: number | null
}

export interface GridElement
  extends ReactDataSheet.Cell<GridElement, CellValue> {
  value: CellValue
}

export interface GridDataSate {
  grid: GridElement[][]
  workQuantityList: WorkQuantity[]
  workQuantityListLastMonth: WorkQuantity[]
  displayModes: boolean[]
}

/** メインのグリッド設定 */
const SheetRenderer: FC<PropsWithChildren<{
  className: string
  simulationYm: string
  years: number
  months: number[]
  displayStartYear: string
  authoritySupplier: boolean
}>
> = ({
  className,
  simulationYm,
  children,
  years,
  months,
  displayStartYear,
  authoritySupplier
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const location = useAppSelector((state) => state.confirmreply.location.supplierDecisionBuildingLocation)
  const locationTop = useAppSelector((state) => state.confirmreply.locationTop.additionalBuildingLocation)
  const dispatch = useAppDispatch()

  const createYearColumns = (startYear: string) => {
    const firstYear = Number(startYear)
    const emptyArray = Array.from({ length: years })

    return emptyArray.map<number>((_value, index) => {
      return firstYear + index
    })
  }

  useLayoutEffect(() => {
    ref.current?.scrollTo({ left: location, top: locationTop })
  }, [location, locationTop])

  return (
    <div
      className="scrollable-container"
      style={{ maxHeight: '61vh' }}
      ref={ref}
      onScroll={() => {
        dispatch(ConfirmReplyActions.setSupplierDecisionBuildingLocation(Number(ref.current?.scrollLeft)))
        dispatch(ConfirmReplyActions.setAdditionalBuildingLocationTop(Number(ref.current?.scrollTop)))
      }}
    >
      <table
        className={className}
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <thead>
          <tr>
            <th rowSpan={2} style={{ position: 'sticky', left: 0 }}>
              <div style={{ width: 30 }}>No</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 33 }}>
              <div style={{ width: 40 }}>非表示</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 76 }}>
              <div style={{ width: 40 }}>削除</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 119 }}>
              <div style={{ width: 40 }}>{authoritySupplier ? '' : '対象外'}</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 162 }}>
              <div style={{ width: 80 }}>管轄支店</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 245 }}>
              <div style={{ width: 690 }}>工事名称</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 938 }}>
              <div style={{ width: 30 }}>検索</div>
            </th>
            {createYearColumns(displayStartYear).map((year) => (
              <th colSpan={12} key={year}>
                {year}年度
              </th>
            ))}
          </tr>
          <tr>
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

/** 左ヘッダの設定 */
const RowRenderer: FC<
PropsWithChildren<{
  expenseCd: string
  displaySelected: boolean
  display: string
  rowNo: number
  rowLength: number
  workQuantity: WorkQuantity
  nonCtrlFlg: boolean
  onAdditionalDisplayCheckboxChange: (selectedRowIndex: number, selected: boolean) => void
  onAdditionalApplicabCheckboxChange: (selectedRowIndex: number, selected: boolean) => void
  onDeleteCheckboxChange: (selectedRowIndex: number, selected: boolean) => void
  onBranchDropDownChange: (val: string, rowNo: number) => void
  onAddBuildingInfoChange: (val: BuildingInfoGet | null, rowNo: number) => void
  authoritySupplier: boolean
}>> = ({
  expenseCd,
  displaySelected,
  display,
  rowNo,
  rowLength,
  workQuantity,
  nonCtrlFlg,
  children,
  onAdditionalDisplayCheckboxChange,
  onAdditionalApplicabCheckboxChange,
  onDeleteCheckboxChange,
  onBranchDropDownChange,
  onAddBuildingInfoChange,
  authoritySupplier
}) => {
  const isLastRow = rowNo === rowLength - 1

  // M1明細の各行に対し、State．新規追加フラグ=1(新規)の場合は赤字で表示する
  const isRed = workQuantity !== undefined ? workQuantity.newAddFlg === 1 : false

  const initialData = useAppSelector((x) => x.common.initialData)
  const branchesData = initialData.codeNames('SI01')

  if (isLastRow) {
    return (
      <tr style={{ height: 33 }}>
        <th
          style={{ position: 'sticky', left: 0, textAlign: 'left' }}
          colSpan={7}
        >
          <span>合計</span>
        </th>
        {children}
      </tr>
    )
  }

  return (
    <tr style={{ height: 33, display: `${display}` }}>
      <th style={{ position: 'sticky', left: 0, color: isRed ? '#F00' : '' }}>
        <span>{rowNo + 1}</span>
      </th>
      <th style={{ position: 'sticky', left: 33 }}>
        <Checkbox
          checked={displaySelected}
          disabled={nonCtrlFlg}
          onChange={(e) => onAdditionalDisplayCheckboxChange(rowNo, e.value)}
        ></Checkbox>
      </th>
      <th style={{ position: 'sticky', left: 76 }}>
        <Checkbox
          checked={workQuantity.delFlg === 1}
          disabled={nonCtrlFlg}
          onChange={(e) => onDeleteCheckboxChange(rowNo, e.value)}
        ></Checkbox>
      </th>
      <th style={{ position: 'sticky', left: 119 }}>
      {!authoritySupplier && (
          <Checkbox
            checked={workQuantity.notApplicableFlg === 1}
            disabled={nonCtrlFlg}
            onChange={(e) => onAdditionalApplicabCheckboxChange(rowNo, e.value)}
          />
      )}
      </th>
      <th style={{ position: 'sticky', left: 162, color: isRed ? '#F00' : '' }}>
        <span style={{ color: isRed ? '#F00' : '' }}>
          {
            branchesData.find((item: CodeName) => {
              return item.CODE === workQuantity.branchCd
            })?.NAME
          }
        </span>
      </th>
      <th style={{ position: 'sticky', left: 245, color: isRed ? '#F00' : '' }}>
        <Tooltip position='right'>
          <span title={workQuantity.constructionNmManual ?? ''} style={{ color: isRed ? '#F00' : workQuantity.remark === null ? '#808080' : '', width: 660, display: 'block', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>{workQuantity.constructionNmManual}</span>
        </Tooltip>
      </th>
      <th
        style={{ position: 'sticky', left: 938, width: '30px' }}
        className="buttons-container"
      >
        <BuildingSelect
          buttonOnly
          handleSelectVal={(val) => {
            onAddBuildingInfoChange(val, rowNo)
          }}
          isBtnDisabled={nonCtrlFlg}
        />
      </th>
      {children}
    </tr>
  )
}

/** セルの設定 */
const ValueViewer: FC<{
  valueViewerProps: ReactDataSheet.ValueViewerProps<GridElement, number>
  rowLength: number
  isRed: boolean
}> = ({ valueViewerProps, rowLength, isRed }) => {
  const isLastRow = valueViewerProps.row === rowLength - 1

  return (
    <div
      style={{
        backgroundColor: isLastRow ? '#99FF99' : '',
        height: '100%',
        color: isRed ? '#F00' : ''
      }}
    >
      {valueViewerProps.value}
    </div>
  )
}
interface AdditionalBuildingGridPprops {
  gridDataState: GridDataSate
  years: number
  months: number[]
  simulationYm: string
  displayStartYear: string
  nonCtrlFlg: boolean
  expenseCd: string
  onCellsChanged: (
    arrayOfChanges: ReactDataSheet.CellsChangedArgs<GridElement, CellValue>,
    arrayOfAdditions?: ReactDataSheet.CellAdditionsArgs<CellValue>
  ) => void
  onAddRowBtnClick: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => void
  onAdditionalDisplayCheckboxChange: (selectedRowIndex: number, selected: boolean) => void
  onAdditionalApplicabCheckboxChange: (selectedRowIndex: number, selected: boolean) => void
  onDeleteCheckboxChange: (selectedRowIndex: number, selected: boolean) => void
  onBranchDropDownChange: (val: string, rowNo: number) => void
  onAddBuildingInfoChange: (val: BuildingInfoGet | null, rowNo: number) => void
}

export const AdditionalBuildingGrid: FC<AdditionalBuildingGridPprops> = (
  props
) => {
  const userProp = useAppSelector((x) => x.common.userProp)
  // ログイン情報．権限区分＝"5"(業者)の場合、M2対象外を非表示します。
  const authoritySupplier = userProp?.authority === 5

  const initialData = useAppSelector((x) => x.common.initialData)
  const expenses = initialData.codeNames('HI01')
  const unit = expenses.find((item: CodeName) => {
    return item.CODE === props.expenseCd
  })?.TXT8
  const sheetRenderer = (
    sheetRendererProps: ReactDataSheet.SheetRendererProps<
    GridElement,
    CellValue
    >
  ) => {
    return (
      <SheetRenderer
        className={sheetRendererProps.className}
        simulationYm={props.simulationYm}
        years={props.years}
        months={props.months}
        displayStartYear={props.displayStartYear}
        authoritySupplier={authoritySupplier}
      >
        {sheetRendererProps.children}
      </SheetRenderer>
    )
  }

  const rowRenderer = (
    rowRendererProps: ReactDataSheet.RowRendererProps<GridElement, CellValue>
  ) => {
    const workQuantity: WorkQuantity =
      props.gridDataState.workQuantityList[rowRendererProps.row]
    const currentRowDisplaySelection =
      workQuantity !== undefined ? workQuantity.hiddenFlg !== 0 : false
    const display = props.gridDataState.displayModes[rowRendererProps.row]
      ? ''
      : 'none'
    return (
      <RowRenderer
        expenseCd={props.expenseCd}
        rowNo={rowRendererProps.row}
        rowLength={props.gridDataState.grid.length}
        workQuantity={workQuantity}
        displaySelected={currentRowDisplaySelection}
        display={display}
        nonCtrlFlg={props.nonCtrlFlg}
        onAdditionalDisplayCheckboxChange={props.onAdditionalDisplayCheckboxChange}
        onAdditionalApplicabCheckboxChange={props.onAdditionalApplicabCheckboxChange}
        onDeleteCheckboxChange={props.onDeleteCheckboxChange}
        onBranchDropDownChange={props.onBranchDropDownChange}
        onAddBuildingInfoChange={props.onAddBuildingInfoChange}
        authoritySupplier={authoritySupplier}
      >
        {rowRendererProps.children}
      </RowRenderer>
    )
  }

  const valueViewer = (
    valueViewerProps: ReactDataSheet.ValueViewerProps<GridElement, number>
  ) => {
    const workQuantity: WorkQuantity = props.gridDataState.workQuantityList[valueViewerProps.row]
    let isRed = false

    if (workQuantity !== undefined) {
      if (workQuantity.newAddFlg === 1) {
        // M1明細の各行に対し、State．新規追加フラグ=1(新規)の場合は赤字で表示する
        isRed = true
      } else {
        const colNo = valueViewerProps.col
        const workQuantityListLastMonth = props.gridDataState.workQuantityListLastMonth
        const lastMonth = workQuantityListLastMonth.find((obj) =>
          obj.procurementNo === workQuantity.procurementNo && obj.subNo === workQuantity.subNo && obj.expenseSeq === workQuantity.expenseSeq)

        // ※対象の支店コードに一致する変数．前月度労務能力．業者追加物件．月別労務数が取得出来る場合
        if (workQuantity !== undefined && lastMonth !== undefined) {
          const simulatedMonth = Number(props.simulationYm.substring(4, props.simulationYm.length))

          // ※データストア．業者ヒアリング遷移パラメータ．パラメータ．シミュレーション年月度が4月度の場合、
          // 変数．前月度労務能力．業者追加物件．月別労務数のIndexを+12する
          if (simulatedMonth === 4) {
            if (colNo + 12 <= workQuantity.workQuantityByMonth.length - 1) {
              isRed = workQuantity.workQuantityByMonth[colNo] !== lastMonth?.workQuantityByMonth[colNo + 12]
            } else {
              isRed = true
            }
          } else {
            isRed = workQuantity.workQuantityByMonth[colNo] !== lastMonth?.workQuantityByMonth[colNo]
          }
        }
      }
    }

    return (
      <ValueViewer
        valueViewerProps={valueViewerProps}
        rowLength={props.gridDataState.grid.length}
        isRed={isRed}
      />
    )
  }

  return (
    <div style={{ position: 'sticky', zIndex: 2 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <h3 style={{ marginBottom: 0 }}>
          業者追加物件（単位：{unit}）
          <span
            style={{ display: 'inline-block', marginLeft: '16px', color: '#d51923', fontWeight: '700', fontSize: '16px', verticalAlign: 'top', whiteSpace: 'pre-line' }}
          >
            {hearingMessage.additionalBuilding.additionalBuildingAnnotationInfo}
          </span>
        </h3>
        {!props.nonCtrlFlg && (
          <Button
            style={{
              marginTop: 24,
              width: '75px',
              height: '30px',
              backgroundColor: '#00307C',
              color: 'white'
            }}
            onClick={props.onAddRowBtnClick}
          >
            行追加
          </Button>
        )}
      </div>
      <ReactDataSheet<GridElement, CellValue>
        data={props.gridDataState.grid}
        sheetRenderer={sheetRenderer}
        rowRenderer={rowRenderer}
        valueRenderer={(cell) => cell.value}
        valueViewer={valueViewer}
        onCellsChanged={props.onCellsChanged}
      />
    </div>
  )
}
